// キャンバス上のオブジェクトの型定義（discriminated union）。
// サーバ・クライアント・CDK の全てがこの型を共有する。

import type { TextInlineStyleRange } from "./text-inline-style";

/**
 * テキスト系オブジェクト（テキスト・付箋）の文字寄せ。
 * 既定は "left"（未指定=左寄せ）。後方互換のため optional とし、旧データは left として扱う。
 */
export type TextAlign = "left" | "center" | "right";

/**
 * 図形の輪郭線とペンストロークの線種。
 * 後方互換のため保存フィールド自体は任意とし、未指定は描画側で実線として扱う。
 */
export type StrokePattern = "solid" | "dotted";

/**
 * 矢印図形の起点（尻側）となる外接矩形の角。終点（矢じり側）は対角の角になる。
 * ドラッグ配置で「ドラッグ開始点→ドロップ点」の向きを保持するために使う。
 * 未指定（旧データ・シングルクリック配置）は従来どおり水平左→右の矢印として描画する。
 */
export type ArrowCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right";

/**
 * 軸そろえ矢印の起点となる辺。矢印は、この辺の中点から対辺の中点へ描画される。
 * 外接矩形を退化させず、水平・垂直の矢印を傾きなく表現するために使う。
 */
export type ArrowEdge = "left" | "right" | "top" | "bottom";

interface BaseCanvasObject {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly rotation?: number | undefined;
  readonly zOrder: number;
  /**
   * オブジェクトのロック状態。true のときフロントエンドは移動・リサイズ・回転・編集・削除を抑止する。
   * 後方互換のため optional とし、既存データでは undefined（=未ロック）として扱う。
   */
  readonly locked?: boolean | undefined;
  readonly version: number;
  readonly createdAt: string;
  readonly createdBy: string;
  readonly updatedAt: string;
  readonly updatedBy: string;
}

/**
 * 本文中の表 1 つぶんの、利用者が決めた列幅。Markdown の記法には列幅の概念が無いため、
 * 本文とは別にテキスト図形・付箋のフィールドとして保存する。
 *
 * `columnCount` と `headerKey` は「保存した時点の表」と「いま本文から解析した表」が同じものかを
 * 確かめるための照合材料で、どちらかが食い違えば保存値を使わず自動列幅へ戻す。本文編集で列が
 * 増減した表へ古い幅を当てて表示を壊さないための、表示時の単純な一致確認である。
 */
export interface TableColumnWidths {
  /** 保存した時点の列数。本文から解析した列数と違う場合、この保存値は使わない。 */
  readonly columnCount: number;
  /**
   * 保存した時点のヘッダ行の指紋（8 桁の小文字 16 進数）。算出規則の正本は
   * `markdown-source-map.ts` の `markdownTableHeaderKey`。ブロック順 index と列数だけで
   * 対応づけると、表の削除・並べ替えで「同じ列数の別の表」へ古い幅が当たるため、
   * index・列数に加えてこの指紋も一致したときだけ保存値を採用する。
   */
  readonly headerKey: string;
  /** 列幅（em）。長さは `columnCount` と一致する。 */
  readonly widthsEm: readonly number[];
}

/**
 * 本文中の表（ブロック順）ごとの列幅。配列の index は本文中の表のブロック順で、
 * まだ調整していない表は `null`（＝自動列幅）で埋める。
 *
 * 疎な index を `undefined` ではなく `null` で表すのは、配列の中の `undefined` が
 * DynamoDB へ保存するときに要素ごと取り除かれて添字がずれるためである
 * （表図形のセル部分書式 {@link TableCellInlineStyles} と同じ理由）。
 */
export type TableColumnWidthsList = readonly (TableColumnWidths | null)[];

export interface StickyCanvasObject extends BaseCanvasObject {
  readonly type: "sticky";
  readonly text: string;
  readonly color?: string | undefined;
  /**
   * 本文中の表ごとの列幅（保存値）。未設定・`null` の表は自動列幅で描く。
   * 後方互換のため optional とし、既存データは未設定として扱う。
   */
  readonly tableColumnWidths?: TableColumnWidthsList | undefined;
  /**
   * 最終更新者の表示名（日本語名）スナップショット。付箋の本文（text）の文字列が変わる更新の
   * ときだけ、サーバが認可済みの操作者表示名からセットする（WebSocket op 経路では
   * op.actorDisplayName、REST / MCP 経路では actor.displayName）。移動・リサイズ・色変更など
   * 本文を変えない操作では更新しない（内容を書いていない人の名前が付箋へ表示されるのを防ぐ）。
   * 新規作成では、作成した操作者の表示名で厳格に上書きする。
   * 後方互換のため optional とし、旧データでは undefined（=表示しない）として扱う。
   */
  readonly updatedByName?: string | undefined;
  /** 文字寄せ（左/中央/右）。未指定は左寄せ扱い。 */
  readonly textAlign?: TextAlign | undefined;
}

export interface ShapeCanvasObject extends BaseCanvasObject {
  readonly type: "shape";
  readonly shapeKind: "circle" | "rectangle" | "arrow";
  readonly fill?: string | undefined;
  readonly stroke?: string | undefined;
  /** 枠線（矢印では線）の種類。未指定は実線。 */
  readonly strokePattern?: StrokePattern | undefined;
  /**
   * 枠線（矢印では線）の太さ（px）。未指定は既定の 2px として描画する。
   * 後方互換のため optional とし、既存データは描画側で既定値を補う。
   */
  readonly strokeWidth?: number | undefined;
  /**
   * 矢印（shapeKind === "arrow"）のドラッグ配置時の向き。外接矩形のどの角を矢印の起点
   * （尻側）にするかを示し、終点（矢じり側）は対角の角になる。未指定は従来どおり水平
   * 左→右で描画する。circle / rectangle では使用しない（無視される）。
   */
  readonly arrowFromCorner?: ArrowCorner | undefined;
  /**
   * 軸そろえ矢印の起点辺。指定時は arrowFromCorner より優先し、対辺の中点へ描画する。
   * 後方互換のため任意とし、circle / rectangle では使用しない（無視される）。
   */
  readonly arrowFromEdge?: ArrowEdge | undefined;
}

/** 図形の枠線幅として外部入力から受け付ける上限（px）。 */
export const SHAPE_STROKE_WIDTH_MAX_PX = 32;

/**
 * 表の 1 行ぶんのセルの部分書式。部分書式を持たないセルは欠落として表す。
 *
 * `undefined` に加えて `null` も許すのは、op の payload が JSON 化で欠落を `null` にするため、
 * および読み出し側が両方を受け取れるようにするためである。
 * **正規化した後（保存する形）は欠落を `null` で表す。** 配列の中の `undefined` は DynamoDB へ
 * 保存するときに要素ごと取り除かれて添字がずれるため、保存形には使えない
 * （理由の正本は `table-cells.ts` の `normalizeTableCellInlineStyles`）。
 */
export type TableCellInlineStylesRow = readonly (
  | readonly TextInlineStyleRange[]
  | null
  | undefined
)[];

/**
 * 表図形のセルごとの部分書式。`cells` と同じ行×列で並び、部分書式を持たないセル・行は
 * 欠落として表す。配列そのものが `undefined` のときは「この表は部分書式を持たない」を表し、
 * 既存データと同じ意味になる。
 *
 * 3 重の入れ子で添字を直接たどると読み書きを間違えやすいため、参照・更新は
 * `table-cells.ts` のヘルパ（`cellInlineStylesAt` / `withCellInlineStyles`）経由で行う。
 */
export type TableCellInlineStyles = readonly (TableCellInlineStylesRow | null | undefined)[];

/**
 * 表図形。cells[0] はヘッダ行。セル本文は生テキストで保存し、表示のときだけ Markdown の
 * インライン装飾（太字・斜体・取消線・インラインコード）を解釈する（編集中は生テキストを見せる）。
 * セルの文字範囲ごとの色・背景・サイズ・太字は `cellInlineStyles` が持つ。
 */
export interface TableCanvasObject extends BaseCanvasObject {
  readonly type: "table";
  /** 長方形のセル配列。先頭行は必ずヘッダ行として描画する。 */
  readonly cells: readonly (readonly string[])[];
  /**
   * セルごとの部分書式（文字範囲だけへ適用する色・背景・サイズ・太字）。
   * `cells` の形を変えずに疎に持つため、部分書式を持たない表は保存内容も描画結果も変わらない。
   * 旧クライアントはこのフィールドを知らないため読み飛ばす（表は本文だけで描かれる）。
   */
  readonly cellInlineStyles?: TableCellInlineStyles | undefined;
  /** 未設定はセル内容から求める自動列幅。指定時は列数と同じ長さの正数配列。 */
  readonly columnFractions?: readonly number[] | undefined;
  readonly color?: string | undefined;
  readonly fontSize?: number | undefined;
  /**
   * 行・列の増減のたびに 1 つ進む番号。セル更新の op が「どの形の表に対して組み立てられたか」を
   * 表すために使う。未設定は 0 として扱う（旧データ互換）。セル本文の更新では進めない
   * （形が変わらないため）。サーバだけが確定ごとに採番し、クライアントは編集元として送るだけで
   * 自分で採番しない。
   */
  readonly structureRevision?: number | undefined;
}

export interface TextCanvasObject extends BaseCanvasObject {
  readonly type: "text";
  readonly text: string;
  readonly color?: string | undefined;
  /**
   * 背景色。未指定は背景なしとして描画する。
   * 明示的な背景なしは "transparent" で保存し、描画へ採用するのは hex 色だけとする。
   */
  readonly fill?: string | undefined;
  readonly fontSize?: number | undefined;
  /** 本文全体の太字。未設定は通常ウェイトとして扱う。 */
  readonly bold?: boolean | undefined;
  /** 文字寄せ（左/中央/右）。未指定は左寄せ扱い。 */
  readonly textAlign?: TextAlign | undefined;
  /**
   * 利用者がリサイズハンドルで高さを明示指定したか。
   * true のとき、高さの自動追随では縮小せず、本文が枠を超えた場合の拡大だけを行う。
   * 後方互換のため optional とし、未設定は手動指定されていないものとして扱う。
   */
  readonly manualHeight?: boolean | undefined;
  /**
   * 文字範囲だけへ適用する部分書式（inline style run）。
   * offset は本文（text）先頭からの UTF-16 code unit 数で、`start` を含み `end` を含まない。
   * 保存形は「start 昇順・非重複・基底書式（color / fill / fontSize / bold）との差分のみ・
   * 隣接同値は結合済み」の正規形に固定する（正本は text-inline-style.ts）。
   * 後方互換のため optional とし、未設定は空配列（＝全文書式だけ）として扱う。
   */
  readonly inlineStyles?: readonly TextInlineStyleRange[] | undefined;
  /**
   * 本文中の表ごとの列幅（保存値）。未設定・`null` の表は自動列幅で描く。
   * 後方互換のため optional とし、既存データは未設定として扱う。
   */
  readonly tableColumnWidths?: TableColumnWidthsList | undefined;
  /**
   * 本文・部分書式・全文テキスト書式の競合境界となる 0 以上の整数。未設定は 0 として扱う。
   * サーバだけが対象 op の確定ごとに 1 増やし、クライアントは編集元として送るだけで採番しない。
   * 移動・リサイズなど本文・書式以外の更新では増やさない。
   */
  readonly textStyleRevision?: number | undefined;
}

export interface ImageCanvasObject extends BaseCanvasObject {
  readonly type: "image";
  readonly src: string;
  readonly alt?: string | undefined;
  /**
   * 永続化される低品質プレースホルダ（LQIP）の data: URL。
   * 本体（src）の読み込み中・失敗時のぼかしフォールバック背景として使う。
   * `attachment.update` で src が公開 URL に差し替わっても上書きされない設計のため、
   * セッションをまたいで（リロード後の参加者でも）ぼかしプレビューを表示できる。
   * 後方互換のため optional とし、未設定のオブジェクト（既存データ）はクライアント側
   * session キャッシュ（attachmentLqips）にフォールバックする。
   */
  readonly placeholderSrc?: string | undefined;
}

export interface VideoCanvasObject extends BaseCanvasObject {
  readonly type: "video";
  readonly src: string;
  readonly mimeType: string;
  /**
   * 永続化される低品質プレースホルダ（LQIP）の data: URL。
   * 画像と同じ意図だが、動画は現状サムネイル抽出を行わず通常は未設定。
   * 将来的にサーバ側でサムネイル抽出を実装する際の領域として確保する。
   */
  readonly placeholderSrc?: string | undefined;
}

export interface LinkCardCanvasObject extends BaseCanvasObject {
  readonly type: "link-card";
  readonly url: string;
  readonly title: string;
  readonly description?: string | undefined;
  readonly imageUrl?: string | undefined;
  readonly siteName?: string | undefined;
}

export interface PenStrokeCanvasObject extends BaseCanvasObject {
  readonly type: "pen-stroke";
  readonly color: string;
  readonly thickness: number;
  /** 線の種類。未指定は実線。 */
  readonly strokePattern?: StrokePattern | undefined;
  /** ストロークを構成するポイントの配列（stage 座標） */
  readonly points: ReadonlyArray<{ x: number; y: number; pressure: number }>;
}

/**
 * スタンプ（絵文字風アイコン）オブジェクト。
 * ワンクリック配置・他オブジェクト同様の移動 / リサイズ / 回転 / ロックを許容する。
 * kind ごとに固有の絵文字グリフを描画する（DOM での font-size スケーリング）。
 * "avatar" はアバター画像を設定済みの利用者が、自分のアイコン画像をスタンプとして貼り付けるための種別。
 * この場合は絵文字グリフではなく avatarSrc（配置時に確定したスナップショット）を画像として描画する。
 */
/** 絵文字グリフで描画するスタンプ種別。定義順がツールバーの表示順（正準順序）を兼ねる。 */
export const EMOJI_STAMP_KINDS = [
  "thumbs-up",
  "heart",
  "party",
  "grin",
  "surprised",
  "pray",
  "eyes",
  "bulb",
  "check",
  "rocket",
  "fire",
  "smile",
  "hundred",
  "plus",
  "cry",
  "sob",
  "disappointed",
  "pensive",
  "broken-heart",
] as const;

/** アバター画像を含む、永続化されうるスタンプ種別のすべて。 */
export const STAMP_KINDS = [...EMOJI_STAMP_KINDS, "avatar"] as const;

export type EmojiStampKind = (typeof EMOJI_STAMP_KINDS)[number];
export type StampKind = (typeof STAMP_KINDS)[number];

export interface StampCanvasObject extends BaseCanvasObject {
  readonly type: "stamp";
  readonly stampKind: StampKind;
  /**
   * stampKind === "avatar" のときに描画するアバター画像（配置時点の自己完結スナップショット）。
   * 全参加者が表示できるよう、安全なラスタ data:image/ のスナップショットを保持する。
   * 型としては optional だが、avatar スタンプでは必須・絵文字スタンプでは禁止という双方向の不変条件を
   * サーバ ObjectSchema が強制する（絵文字スタンプでは常に undefined）。
   */
  readonly avatarSrc?: string | undefined;
  /**
   * 作成者（スタンプを置いた人）の表示名（日本語名）スナップショット。object.create 時にサーバが
   * createdBy（＝認可済み actorUserId へ固定）とあわせて op.actorDisplayName から厳格に上書きしてセットする
   * （コメントスレッドの作成者情報固定と同方針。クライアント値は採用しない＝なりすまし防止）。
   * 選択時に「誰が置いたスタンプか」を吹き出しで表示するために使う。作成者がオフラインでも
   * 表示できるよう永続スナップショットとして保持する。後方互換のため optional とし、旧データでは
   * undefined（=作成時はプレゼンスからのフォールバック解決、それも無ければ非表示）として扱う。
   * なお削除 undo/redo による復元も object.create として扱われるため、復元では作成者・作成者名が
   * 復元を行ったアクターへ付け替わる（原作成者を安全に保つサーバ権威の復元来歴シグナルは持たない）。
   */
  readonly createdByName?: string | undefined;
}

/**
 * コメントスレッド内の 1 メッセージ（起点コメント・返信を含む）。
 * 投稿者名・アバターは投稿時点のスナップショットを保持し、投稿者が現在オフラインでも
 * ピンや返信ポップオーバーで表示できるようにする（付箋の updatedByName 方針と同様）。
 * authorId / authorName はサーバが op.actorUserId / actorDisplayName で厳格に上書きする。
 * authorAvatar は接続時アバターを優先し、欠落時だけ自己完結ラスタ data URL と検証できた
 * クライアント値を採用する。
 */
export interface CommentMessage {
  readonly id: string;
  /** 投稿者の userId。編集・削除の権限判定（本人 or 管理者）に使う。 */
  readonly authorId: string;
  /** 投稿時点の表示名スナップショット。 */
  readonly authorName: string;
  /** 投稿時点のアバター URL スナップショット（任意）。data: か信頼済み IdP の https のみ描画する。 */
  readonly authorAvatar?: string | undefined;
  readonly body: string;
  /**
   * 添付画像の公開 URL（添付基盤 attachments/<canvasId>/... へ昇格済みのもの）。
   * 画像だけのメッセージ（body 空）も許容する。上限枚数は COMMENT_IMAGES_MAX。
   * サーバは comment.message.add / object.create（comment-thread）の検証でスキーム・
   * 長さ・枚数を制限し、編集（comment.message.update）では本文のみ変更して images は維持する。
   */
  readonly images?: ReadonlyArray<string> | undefined;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** 編集された場合のみセット（編集済みバッジ表示用）。未設定=未編集。 */
  readonly editedAt?: string | undefined;
}

/**
 * コメントスレッド（FigJam 風コメントピン）オブジェクト。
 * x / y を吹き出しピンのアンカー（クリック位置）とし、ビューポート固定サイズのオーバーレイ層で
 * 描画する（通常オブジェクトの zoom 拡縮・選択・整列・クリップボード等の対象外）。
 * messages は時系列の配列で、先頭が起点コメント。スレッド内の全メッセージが削除されると
 * オブジェクトごと削除される。複数投稿者がいる場合はピンに各投稿者のアバターを重ねて表示する。
 */
export interface CommentThreadCanvasObject extends BaseCanvasObject {
  readonly type: "comment-thread";
  readonly messages: ReadonlyArray<CommentMessage>;
}

/**
 * コネクタ（接続矢印）が図形のどの辺へ接続するかを表す向き。
 * 接続アンカーは対象オブジェクトの外接矩形（AABB）における各辺の中点とする。
 */
export type ConnectorSide = "top" | "right" | "bottom" | "left";

/**
 * コネクタの端点。接続先オブジェクトの id と、その図形のどの辺に接続するか（side）を持つ。
 */
export interface ConnectorEndpoint {
  readonly objectId: string;
  readonly side: ConnectorSide;
}

/**
 * コネクタ（図形と図形を結ぶ矢印）オブジェクト。
 * 端点 from / to はそれぞれ別オブジェクトの辺へ接続し、表示時は両端の現在位置から
 * 直交折れ線でルーティングして描画する（クライアント側でリアルタイム再計算）。
 * x / y / w / h は作成時点のルート外接矩形を保持するが、描画・当たり判定は端点から
 * 都度計算するため、接続先が移動しても保存値の更新は不要（カリングと保存制約の参考値）。
 */
export interface ConnectorCanvasObject extends BaseCanvasObject {
  readonly type: "connector";
  readonly from: ConnectorEndpoint;
  readonly to: ConnectorEndpoint;
  /** 線色。未指定はクライアント既定色で描画する。 */
  readonly stroke?: string | undefined;
  /** 線の太さ（px）。未指定はクライアント側で既定の 2px として描画する。 */
  readonly strokeWidth?: number | undefined;
}

/**
 * セクション（Confluence ホワイトボード風の領域コンテナ）オブジェクト。
 * タイトル付きの角丸矩形で、キャンバス上の領域に名前を付けて区切る。
 * - 通常オブジェクトより常に背面に描画する（レンダリング層でセクション帯 / 通常帯に分離）。
 * - 移動時は「矩形に一部でも（正の面積で）重なり、かつセクションより前面に描かれるオブジェクト」を
 *   一緒に移動する。別のセクションは、矩形に完全に含まれる入れ子のセクションだけを描画順に関わらず
 *   一緒に移動する（親子関係は永続化せず、ドラッグ開始時に幾何計算で決定する。判定は
 *   collectObjectsCarriedByMove を参照）。Alt+ドラッグ複製で一緒に複製するのは、矩形に AABB が
 *   完全に含まれるオブジェクトだけ（判定は collectSectionContainedObjectIds を参照）。
 * - タイトルは text フィールドに保持する（object.text.update op を汎用流用するため。単一行運用）。
 * - 背景色は fill に保持する（object.style.update の既存 fill キーを流用。未設定は既定色で描画）。
 * - 回転は非対応（UI を出さず、サーバは transform から rotation を strip する）。
 */
export interface SectionCanvasObject extends BaseCanvasObject {
  readonly type: "section";
  /** セクションのタイトル（単一行。空文字を許容する）。 */
  readonly text: string;
  /** 背景色。未指定はクライアント既定色（SECTION_DEFAULT_FILL）で描画する。 */
  readonly fill?: string | undefined;
}

export type CanvasObject =
  | StickyCanvasObject
  | SectionCanvasObject
  | ShapeCanvasObject
  | TableCanvasObject
  | TextCanvasObject
  | ImageCanvasObject
  | VideoCanvasObject
  | LinkCardCanvasObject
  | PenStrokeCanvasObject
  | StampCanvasObject
  | ConnectorCanvasObject
  | CommentThreadCanvasObject;

export type CanvasObjectType = CanvasObject["type"];

/**
 * 添付（画像・動画・リンクカード）の現在の URL を返す。添付を持たない種別は undefined。
 */
export function currentAttachmentSrc(object: CanvasObject): string | undefined {
  if (object.type === "image" || object.type === "video") return object.src;
  if (object.type === "link-card") return object.imageUrl;
  return undefined;
}

/**
 * アップロード確定前のローカルプレースホルダ URL（`data:` / `blob:`）かを判定する。
 * 画像・動画の貼り付けは「プレースホルダ src で作成 → アップロード完了で公開 URL へ差し替え／
 * 失敗で削除」という 2 段構成のため、サーバ側のロックガードが「未確定の一時オブジェクトか」を
 * 判断する共通の正本として使う（WebSocket 経路・REST / MCP 経路で同じ判定にそろえる）。
 */
export function isUnresolvedAttachmentSrc(src: string | undefined): boolean {
  if (typeof src !== "string") return false;
  return src.startsWith("data:") || src.startsWith("blob:");
}
